/**
 * Romanian — the source dictionary. Every UI string lives here; en.ts and it.ts
 * mirror these keys. `{name}` placeholders are filled by t(key, params).
 */
export const ro = {
  'lang.name': 'Română',
  'lang.aria': 'Limbă',
  'app.title': 'Sintetizator web',

  // Header — mapping shift
  'header.shift.aria': 'Mută maparea tastaturii',
  'shift.octDown': '− 8ve',
  'shift.noteDown': '− notă',
  'shift.noteUp': '+ notă',
  'shift.octUp': '+ 8ve',
  'shift.octDown.title': 'O octavă mai jos (tasta −)',
  'shift.noteDown.title': 'O notă albă mai jos (tasta _)',
  'shift.noteUp.title': 'O notă albă mai sus (tasta +)',
  'shift.octUp.title': 'O octavă mai sus (tasta =)',

  // Install button
  'install.prompt': 'Instalează aplicația',
  'install.installed': 'Instalată',
  'install.promptTitle': 'Instalează sintetizatorul ca aplicație',
  'install.uninstallHint':
    'Aplicația e instalată. Browserele nu permit dezinstalarea dintr-o pagină; folosește meniul browserului (⋮) → „Dezinstalează”, sau aplicațiile sistemului.',
  'install.manualHint':
    'Dacă nu apare automat: din meniul browserului (⋮) alege „Instalează aplicația”. Pe iPhone/iPad: Distribuie → „Adaugă pe ecranul principal”.',

  // Metronome
  'metronome.label': 'Metronom',
  'metronome.tempoDown.aria': 'Tempo mai mic',
  'metronome.tempoUp.aria': 'Tempo mai mare',
  'metronome.bpm.aria': 'Tempo (BPM)',
  'metronome.tap': 'Tap',
  'metronome.tap.aria': 'Bate tempo cu clic',
  'metronome.start': 'Pornește',
  'metronome.stop': 'Oprește',
  'meter.aria': 'Ritm (semnătură de măsură)',
  'meter.minus.aria': 'Mai puține pulsuri',
  'meter.plus.aria': 'Mai multe pulsuri',
  'meter.accents.aria': 'Accente pe pulsuri',
  'meter.custom': 'Personalizat…',

  'sustain': 'Sustain',
  'reset.all': 'Resetează toate setările',

  // Section titles
  'section.keyboard': 'Claviatură',
  'section.engine': 'Motor de sunet',
  'section.sound': 'Instrument și sunet',
  'section.effects': 'Efecte',
  'section.curve': 'Curbă de nivel pe registru (bas ↔ acut)',
  'section.offline': 'Disponibilitate offline',

  // Keyboard section
  'labels.aria': 'Etichete pe taste',
  'label.cde': 'CDE',
  'label.cde.title': 'Litere pe taste (C D E …)',
  'label.doremi': 'DoReMi',
  'label.doremi.title': 'Solfegiu pe taste (Do Re Mi …)',
  'label.keys': 'Taste',
  'label.keys.title': 'Tasta de pe tastatură, pe fiecare clapă',
  'label.octave': 'Octavă',
  'label.octave.title': 'Numărul octavei, sus pe clapă',
  'field.startNote': 'Notă de start',
  'field.startOctave': 'Octavă de start',
  'field.octaveCount': 'Număr de octave',
  'field.displayMode': 'Mod afișare',
  'display.scroll': 'Derulare orizontală',
  'display.fit1': 'Scalată pe un rând',
  'display.fit2': 'Scalată pe două rânduri',
  'range.label': 'Interval claviaturii',
  'range.keys': '{n} taste',
  'range.low.aria': 'Nota cea mai gravă',
  'range.high.aria': 'Nota cea mai acută',
  'range.drag.aria': 'Mută tot intervalul',
  'octaveCount.full': 'Interval complet (88 taste)',
  'octaveCount.partial': '— (interval parțial)',

  // Engine section
  'field.engine': 'Motor',
  'engine.synth': 'Sintetizat (Tone.js)',
  'engine.sampler': 'Sample real (instrumente înregistrate)',
  'field.synthType': 'Tip sintetizator',
  'field.preset': 'Preset',
  'preset.aria': 'Preset instrument',
  'field.waveform': 'Timbru',
  'waveform.aria': 'Formă undă (timbru)',
  'field.instrument': 'Instrument',
  'field.density': 'Densitate sample-uri',
  'density.6': 'Rapid (1 sample / 6 semitonuri)',
  'density.3': 'Echilibrat (1 sample / 3 semitonuri)',
  'density.1': 'Complet (toate sample-urile)',

  // Sampler status
  'status.loading': 'Se încarcă „{instrument}”: {loaded} / {total} sample-uri…',
  'status.upgrading': 'Redabil acum ({count} sample-uri) · se încarcă calitatea completă…',
  'status.ready': 'Gata: {count} sample-uri ({low}–{high}){failed}. Notele dintre ele sunt transpuse.',
  'status.readyFailed': ' · {failed} eșuate',
  'status.cached': 'Instrument încărcat (din cache).',
  'status.errNoSamples': 'Instrumentul nu are sample-uri în librărie.',
  'status.errLoadFailed': 'Nu s-a putut încărca niciun sample (verifică conexiunea).',

  // Keyboard loading overlay
  'overlay.loading': 'Se încarcă „{instrument}”… {loaded} / {total}',
  'overlay.preparing': 'Se pregătește instrumentul…',
  'overlay.error': 'Nu s-au putut încărca sample-urile: {message}',

  // Sound knobs
  'knob.attack': 'Atac (s)',
  'knob.release': 'Declin (s)',
  'knob.volume': 'Volum',
  'knob.filter': 'Luminozitate',
  'knob.filter.aria': 'Luminozitate (filtru trece-jos)',
  'knob.echo': 'Echo',
  'knob.reverb': 'Reverb',
  'resetInstrument': 'Resetează la implicit',
  'resetInstrument.title': 'Readuce sunetul acestui instrument la valorile implicite',

  // Filter tone words
  'filter.veryDark': 'f. întunecat',
  'filter.dark': 'întunecat',
  'filter.neutral': 'neutru',
  'filter.bright': 'luminos',
  'filter.veryBright': 'f. luminos',

  // Register curve
  'curve.hint':
    'Compensare de sonoritate percepută pentru oscilatoarele sintetizate, care sunt plate din punct de vedere tehnic dar sună sărace în bas. Instrumentele înregistrate își aduc propriul echilibru (măsurat plat în ~2,7 dB de la C1 la C7), așa că implicit curba nu li se aplică.',
  'curve.bassPre': 'Bas:',
  'curve.bassPost': 'dB / octavă sub Do4',
  'curve.treblePre': 'Acut:',
  'curve.treblePost': 'dB / octavă peste Do4',
  'curve.onSamples': 'Aplică și pe sample-uri reale',
  'curve.notApplied': 'Curba nu se aplică motorului de sample-uri (bifează caseta pentru a o aplica).',

  // Offline section
  'offline.hint':
    'Instalează aplicația din meniul browserului („Instalează” / „Adaugă pe ecranul principal”), apoi descarcă toate sample-urile reale în cache. După aceea sintetizatorul funcționează complet offline, pe orice device. Descărcarea are ordinul a ~100–250 MB și se face o singură dată.',
  'offline.download': 'Descarcă toate pentru offline',
  'offline.refresh': 'Reîmprospătează cache-ul offline',
  'offline.done': 'Toate sample-urile au fost descărcate — disponibile offline.',
  'offline.downloading': 'Se descarcă {done} / {total} sample-uri…{failed}',
  'offline.failedSuffix': ' ({failed} eșuate)',
  'offline.doneAll': 'Gata — toate cele {total} sample-uri sunt disponibile offline.',
  'offline.doneWithErrors': 'Descărcat cu {failed} erori — reîncearcă pentru a completa cache-ul.',

  // Engine notices
  'engine.fallback': '„{label}” nu a putut fi creat ({reason}). S-a revenit la Synth.',
  'worklet.hint':
    'Pagina e deschisă direct de pe disc (file://), unde browserul refuză modulele AudioWorklet: PluckSynth și BitCrusher nu pot funcționa. Rulează „npm run dev” și deschide adresa http afișată acolo ca să meargă.',

  // Keyboard area
  'keyboard.aria': 'Claviatură',
  'keyboard.resize.aria': 'Reglează înălțimea claviaturii (trage, sau săgeți sus/jos; dublu-clic = automat)',
  'keyboard.resize.title': 'Trage pentru înălțime · dublu-clic = automat',

  // Presets
  'preset.piano': 'Pian',
  'preset.organ': 'Orgă',
  'preset.strings': 'Coarde',
  'preset.bass': 'Bas',
  'preset.lead': 'Lead',
  'preset.custom': 'Personalizat',

  // Synth types
  'synth.synth': 'Synth',
  'synth.monosynth': 'MonoSynth',
  'synth.amsynth': 'AMSynth',
  'synth.fmsynth': 'FMSynth',
  'synth.duosynth': 'DuoSynth',
  'synth.membrane': 'MembraneSynth',
  'synth.metal': 'MetalSynth',
  'synth.noise': 'NoiseSynth (monofonic)',
  'synth.pluck': 'PluckSynth (monofonic)',

  // Waveforms
  'waveform.sine': 'Sinusoidal',
  'waveform.triangle': 'Triunghiular',
  'waveform.sawtooth': 'Dinte de fierăstrău',
  'waveform.square': 'Pătrat',

  // Effects
  'effect.mix': 'Mix',
  'effect.chorus': 'Chorus',
  'effect.distortion': 'Distorsiune',
  'effect.bitcrusher': 'BitCrusher',
  'effect.vibrato': 'Vibrato',
  'effect.autowah': 'AutoWah',
  'effect.phaser': 'Phaser',
  'effect.pingpong': 'Ping-pong delay',
  'effect.stereowidener': 'Stereo widener',
  'effect.eq3': 'EQ pe 3 benzi',
  'effect.pitchshift': 'Pitch shift',

  // Parameters (shared by effects and synths, keyed by param key)
  'param.frequency': 'Rată (Hz)',
  'param.distortion': 'Cantitate',
  'param.bits': 'Biți',
  'param.depth': 'Adâncime',
  'param.octaves': 'Octave',
  'param.delayTime': 'Timp (s)',
  'param.width': 'Lățime',
  'param.low': 'Grav (dB)',
  'param.mid': 'Mediu (dB)',
  'param.high': 'Acut (dB)',
  'param.pitch': 'Semitonuri',
  'param.harmonicity': 'Armonicitate',
  'param.modulationIndex': 'Index modulație',
  'param.vibratoAmount': 'Vibrato',
  'param.pitchDecay': 'Pitch decay (s)',
  'param.dampening': 'Amortizare (Hz)',
  'param.resonance': 'Rezonanță',
  'param.type': 'Culoare zgomot',

  // Instruments
  'instrument.piano': 'Pian',
  'instrument.guitar-acoustic': 'Chitară acustică',
  'instrument.guitar-nylon': 'Chitară cu corzi de nylon',
  'instrument.guitar-electric': 'Chitară electrică',
  'instrument.bass-electric': 'Bas electric',
  'instrument.bassoon': 'Fagot',
  'instrument.cello': 'Cello',
  'instrument.clarinet': 'Clarinet',
  'instrument.contrabass': 'Contrabas',
  'instrument.flute': 'Flaut',
  'instrument.french-horn': 'Corn francez',
  'instrument.harmonium': 'Harmonium',
  'instrument.harp': 'Harpă',
  'instrument.organ': 'Orgă',
  'instrument.saxophone': 'Saxofon',
  'instrument.trombone': 'Trombon',
  'instrument.trumpet': 'Trompetă',
  'instrument.tuba': 'Tubă',
  'instrument.violin': 'Vioară',
  'instrument.xylophone': 'Xilofon',

  // Meters
  'meter.4/4': '4/4 (comună)',
  'meter.3/4': '3/4 (vals)',
  'meter.2/4': '2/4',
  'meter.6/8': '6/8 (3+3)',
  'meter.5/4': '5/4 (Take Five · 3+2)',
  'meter.7/8': '7/8 (2+2+3)',
  'meter.9/8': '9/8 (Balcanic · 2+2+2+3)',
};
